<script setup>
import ProductsGrid from "@/components/molecules/ProductsGrid.vue";
import Pagination from "~/components/molecules/Pagination.vue";
import {onActivated, onDeactivated} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import ProductsFilter from "~/components/organisms/ProductsFilter.vue";
import {useCustomFetch} from '~/composables/customFetch.js';

const route = useRoute()
const router = useRouter()
const props = defineProps({
  data: Object,
})

const normalizePage = (value, maxPages = null) => {
  const page = parseInt(value, 10)
  if (!Number.isFinite(page) || page < 1) {
    return 1
  }
  if (maxPages && page > maxPages) {
    return maxPages
  }
  return page
}

const products = ref(props.data.products_data.products)
const total = ref(props.data.products_data.total)
const pages = ref(props.data.products_data.pages)
const count = ref(props.data.products_data.count)
const per_page = ref(props.data.products_data.per_page)
const choseFilters = ref(props.data.chosen_filter)
const categoryId = ref(props.data.category_id ?? null)
const listOffset = ref((normalizePage(route.query.page ?? 1) - 1) * (props.data.products_data.per_page || 0))

const normalizePath = (path) => {
  const cleaned = String(path || '').replace(/\/+$/, '')
  return cleaned.startsWith('/') ? cleaned : `/${cleaned}`
}

const pathHasFilters = (path) =>
  String(path || '').split('/').some((part) => part.includes('='))

const categoryBaseFromPath = (path) =>
  String(path || '')
    .split('/')
    .filter(Boolean)
    .filter((part) => !part.includes('='))
    .join('/')

const page = ref(normalizePage(route.query.page ?? 1))
const sorting = ref(route.query.sorting ?? 'recommended');
const search = computed(() => route.query.search ?? null);
const slug = ref(normalizePath('/' + (Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug)));
const openFilter = ref(0);

const grid = ref(null);
const filterComponent = ref(null)
const loading = ref(false)

// KeepAlive: watchers keep running while deactivated. Guard so opening a product
// cannot rewrite this grid's slug/products (that was resetting load-more state).
let isActive = true
let syncingUrl = false
let skipNextSlugFetch = false

const gridCategoryBase = computed(() => categoryBaseFromPath(slug.value))

const routerPathFromSlug = () => {
  const path = normalizePath(slug.value)
  return path === '/' ? '/' : `${path}/`
}

const isCurrentCategoryRoute = () => {
  if (!route.path.startsWith('/') || route.path.startsWith('/shop/')) {
    return false
  }
  const routeBase = categoryBaseFromPath(
    Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug
  )
  return Boolean(routeBase) && routeBase === gridCategoryBase.value
}

watch(() => page.value, () => {
  if (!isActive || syncingUrl) {
    return
  }
  const query = {...route.query}
  if (page.value > 1) {
    query.page = String(page.value)
  } else {
    delete query.page
  }
  router.replace({path: routerPathFromSlug(), query})
})

watch(() => sorting.value, () => {
  if (!isActive || syncingUrl) {
    return
  }
  router.replace({
    path: routerPathFromSlug(),
    query: {
      ...route.query,
      sorting: sorting.value,
    },
  })

  window.dispatchEvent(new CustomEvent('urlReplaced', {
    detail: {message: ''},
  }))
})

watch(() => slug.value, () => {
  if (!isActive || syncingUrl || skipNextSlugFetch) {
    skipNextSlugFetch = false
    return
  }
  fetchProducts()
})

const loadMore = (newPage) => {
  page.value = newPage
  fetchProducts(true)
}

const changePage = (newPage) => {
  page.value = newPage
  fetchProducts(false)
}

const changeSorting = (newSorting) => {
  sorting.value = newSorting
  page.value = 1
  fetchProducts(false)
}

const filterChanged = (e) => {
  // router.replace already happened in ProductsFilter; avoid a second products fetch
  // from the slug watcher when only filters changed (fetch is triggered by filter flow
  // via slug watch only when skip is off — filterChanged sets slug intentionally).
  const nextSlug = normalizePath(e.slug)
  if (normalizePath(slug.value) === nextSlug) {
    page.value = 1
    choseFilters.value = e.chosenFilters
    return
  }
  slug.value = nextSlug
  page.value = 1
  choseFilters.value = e.chosenFilters
}

const removeFilter = (e) => {
  filterComponent.value.handleClick(e)
}

const removeRangeFilter = (e) => {
  filterComponent.value.handleRangeChange({ name: e.name, value: null })
}

const clearFilter = (e) => {
  filterComponent.value.clearFilter(e)
}

const fetchProducts = async (concatenate = false) => {
  loading.value = true
  page.value = normalizePage(page.value, pages.value || null)

  useCustomFetch(`/product-category${slug.value}`, {
    lazy: false,
    query: {
      page: page.value,
      sorting: sorting.value,
      ...(search.value ? {search: search.value} : {})
    }
  }, true).then(({data}) => {
    if (concatenate) {
      products.value = products.value.concat(data.data.products_data.products)
      listOffset.value = 0
    } else {
      products.value = data.data.products_data.products
      listOffset.value = (normalizePage(page.value) - 1) * (data.data.products_data.per_page || per_page.value || 0)
    }

    if (data.data.category_id != null) {
      categoryId.value = data.data.category_id
    }

    total.value = data.data.products_data.total
    pages.value = data.data.products_data.pages
    count.value = data.data.products_data.count
    per_page.value = data.data.products_data.per_page
    page.value = normalizePage(page.value, pages.value || null)

    if (!concatenate) {
      grid.value?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }

    loading.value = false
  })
}

/**
 * Reconcile grid state with the URL only when this category is active.
 * KeepAlive restore (same path) must be a no-op so load-more + scroll stay intact.
 */
const syncWithBrowserUrl = async () => {
  if (!process.client || !isActive || !isCurrentCategoryRoute()) {
    return
  }

  await nextTick()

  const routePath = normalizePath(
    '/' + (Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug)
  )
  const targetPath = routePath
  const targetHasFilters = pathHasFilters(targetPath)
  const currentPath = normalizePath(slug.value)
  const hasChosenFilters = Object.keys(choseFilters.value || {}).length > 0

  // Same URL as kept state — do not refetch products/scroll.
  // Still hydrate chosenFilters when URL has filters but state is empty.
  if (currentPath === targetPath) {
    if (!targetHasFilters && hasChosenFilters) {
      choseFilters.value = {}
    } else if (targetHasFilters && !hasChosenFilters && filterComponent.value?.syncFromPath) {
      try {
        const synced = await filterComponent.value.syncFromPath(targetPath)
        choseFilters.value = synced?.chosenFilters || {}
      } catch (e) {
        // ignore
      }
    }
    return
  }

  syncingUrl = true
  skipNextSlugFetch = true
  slug.value = targetPath
  page.value = normalizePage(route.query.page ?? 1)
  sorting.value = route.query.sorting ?? 'recommended'
  await nextTick()
  syncingUrl = false

  if (filterComponent.value?.syncFromPath) {
    try {
      const synced = await filterComponent.value.syncFromPath(targetPath)
      choseFilters.value = targetHasFilters ? (synced?.chosenFilters || {}) : {}
    } catch (e) {
      if (!targetHasFilters) {
        choseFilters.value = {}
      }
    }
  } else if (!targetHasFilters) {
    choseFilters.value = {}
  }

  // Path actually changed while active (e.g. in-page filter via back/forward) — reload list.
  fetchProducts(false)
}

onActivated(() => {
  isActive = true
  syncWithBrowserUrl()
})

onDeactivated(() => {
  isActive = false
})

onMounted(() => {
  isActive = true
  syncWithBrowserUrl()
})

watch(
  () => (Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug),
  () => {
    if (!isActive) {
      return
    }
    syncWithBrowserUrl()
  }
)
</script>

<template>
  <div ref="grid">
    <ProductsFilter :openFilter="openFilter" ref="filterComponent" @change="filterChanged"/>
    <ProductsGrid @sortingChanged="changeSorting($event)" :total="total" @removeFilter="removeFilter"
                  @removeRangeFilter="removeRangeFilter"
                  @clearFilter="clearFilter"
                  @filter="openFilter=Math.random() * 100"
                  :currentSorting="sorting"
                  :chosenFilters="choseFilters"
                  :filterTitles="filterComponent?.filterTitles ?? {}"
                  :category-id="categoryId"
                  :page="page"
                  :list-offset="listOffset"
                  :products="products"/>

    <Pagination :key="sorting" @change="changePage" :entity="$t('product.products')" :current-page="page"
                :count="count"
                :url="slug"
                :loading="loading"
                :loadMore="true" @more="loadMore"
                :items-per-page="per_page" :total-items="total"
                :total-pages="pages"/>
  </div>
</template>
